import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Grid, Accordion, AccordionSummary, AccordionDetails,
  Typography, Chip, IconButton, Tooltip, Switch, FormControlLabel,
  Alert, CircularProgress
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DirectionsBoat as BoatIcon,
  Badge as BadgeIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import { DataTable, LoadingState, ConfirmDialog } from '../shared';
import useNotification from '../../hooks/useNotification';
import { pescaService } from '../../services/pescaService';

const MaestrosTab = () => {
  const { empresaActiva: empresa } = useSelector(state => state.empresa);
  const { showNotification } = useNotification();

  // Data
  const [naves, setNaves] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [tripulantes, setTripulantes] = useState([]);

  // Loading
  const [cargando, setCargando] = useState({ naves: false, cargos: false, tripulantes: false });

  // Dialog state
  const [dialog, setDialog] = useState({ open: false, tipo: null, data: null });
  const [guardando, setGuardando] = useState(false);

  // Delete confirm
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null });
  const [eliminando, setEliminando] = useState(false);

  // Accordion expanded
  const [expanded, setExpanded] = useState('naves');

  const rutEmpresa = empresa?.rut || empresa?.rut_empresa;

  // Load functions
  const cargarNaves = useCallback(async () => {
    if (!rutEmpresa) return;
    setCargando(prev => ({ ...prev, naves: true }));
    try {
      const data = await pescaService.cargarNaves(rutEmpresa);
      setNaves(data || []);
    } catch (err) {
      showNotification('Error al cargar naves', 'error');
    } finally {
      setCargando(prev => ({ ...prev, naves: false }));
    }
  }, [rutEmpresa, showNotification]);

  const cargarCargos = useCallback(async () => {
    if (!rutEmpresa) return;
    setCargando(prev => ({ ...prev, cargos: true }));
    try {
      const data = await pescaService.cargarCargos(rutEmpresa);
      setCargos(data || []);
    } catch (err) {
      showNotification('Error al cargar cargos', 'error');
    } finally {
      setCargando(prev => ({ ...prev, cargos: false }));
    }
  }, [rutEmpresa, showNotification]);

  const cargarTripulantes = useCallback(async () => {
    if (!rutEmpresa) return;
    setCargando(prev => ({ ...prev, tripulantes: true }));
    try {
      const data = await pescaService.cargarTripulantes(rutEmpresa, false);
      setTripulantes(data || []);
    } catch (err) {
      showNotification('Error al cargar tripulantes', 'error');
    } finally {
      setCargando(prev => ({ ...prev, tripulantes: false }));
    }
  }, [rutEmpresa, showNotification]);

  useEffect(() => {
    if (rutEmpresa) {
      cargarNaves();
      cargarCargos();
      cargarTripulantes();
    }
  }, [rutEmpresa, cargarNaves, cargarCargos, cargarTripulantes]);

  // Dialog handlers
  const abrirDialog = (tipo, data = null) => {
    const defaults = {
      nave: { nombre: '', activo: true },
      cargo: { nombre: '', factor_participacion: 1.0 },
      tripulante: { nombre: '', rut: '', dv: '', cargo_id: '', activo: true },
    };
    setDialog({ open: true, tipo, data: data || defaults[tipo] });
  };

  const cerrarDialog = () => {
    setDialog({ open: false, tipo: null, data: null });
  };

  const handleFieldChange = (field, value) => {
    setDialog(prev => ({ ...prev, data: { ...prev.data, [field]: value } }));
  };

  const guardar = async () => {
    setGuardando(true);
    try {
      const { id, ...rest } = dialog.data;
      const payload = { ...rest, rut_empresa: rutEmpresa };
      const esEdicion = !!id;

      if (dialog.tipo === 'nave') {
        if (esEdicion) await pescaService.actualizarNave(id, payload);
        else await pescaService.guardarNave(payload);
        await cargarNaves();
      } else if (dialog.tipo === 'cargo') {
        payload.factor_participacion = parseFloat(payload.factor_participacion) || 1.0;
        if (esEdicion) await pescaService.actualizarCargo(id, payload);
        else await pescaService.guardarCargo(payload);
        await cargarCargos();
      } else if (dialog.tipo === 'tripulante') {
        if (payload.cargo_id) payload.cargo_id = parseInt(payload.cargo_id);
        if (esEdicion) await pescaService.actualizarTripulante(id, payload);
        else await pescaService.guardarTripulante(payload);
        await cargarTripulantes();
      }
      showNotification('Guardado correctamente', 'success');
      cerrarDialog();
    } catch (err) {
      showNotification(`Error al guardar: ${err.message || err}`, 'error');
    } finally {
      setGuardando(false);
    }
  };

  const eliminarCargo = async () => {
    if (!deleteConfirm.id) return;
    setEliminando(true);
    try {
      await pescaService.eliminarCargo(deleteConfirm.id);
      showNotification('Cargo eliminado', 'success');
      setDeleteConfirm({ open: false, id: null });
      await cargarCargos();
    } catch (err) {
      showNotification(`Error al eliminar: ${err.message || err}`, 'error');
    } finally {
      setEliminando(false);
    }
  };

  // Column definitions
  const colNaves = [
    { field: 'id', headerName: 'ID', minWidth: 60 },
    { field: 'nombre', headerName: 'Nombre', minWidth: 200 },
    {
      field: 'activo', headerName: 'Estado', minWidth: 100,
      renderCell: (val) => (
        <Chip label={val ? 'Activo' : 'Inactivo'} color={val ? 'success' : 'default'} size="small" />
      ),
    },
  ];

  const colCargos = [
    { field: 'id', headerName: 'ID', minWidth: 60 },
    { field: 'nombre', headerName: 'Cargo', minWidth: 200 },
    {
      field: 'factor_participacion', headerName: 'Factor', minWidth: 100,
      renderCell: (val) => val?.toFixed(2),
    },
  ];

  const colTripulantes = [
    { field: 'id', headerName: 'ID', minWidth: 60 },
    { field: 'nombre', headerName: 'Nombre', minWidth: 200 },
    { field: 'rut', headerName: 'RUT', minWidth: 120, renderCell: (val, row) => `${val}-${row.dv}` },
    { field: 'cargo_id', headerName: 'Cargo', minWidth: 150, renderCell: (val) => {
      const cargo = cargos.find(c => c.id === val);
      return cargo ? cargo.nombre : '-';
    }},
    {
      field: 'activo', headerName: 'Estado', minWidth: 100,
      renderCell: (val) => (
        <Chip label={val ? 'Activo' : 'Inactivo'} color={val ? 'success' : 'default'} size="small" />
      ),
    },
  ];

  const editAction = (tipo) => [
    {
      tooltip: 'Editar',
      icon: <EditIcon fontSize="small" />,
      onClick: (row) => abrirDialog(tipo, row),
    },
  ];

  if (!empresa) return null;

  return (
    <Box sx={{ p: 2 }}>
      {/* NAVES */}
      <Accordion expanded={expanded === 'naves'} onChange={() => setExpanded(expanded === 'naves' ? '' : 'naves')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <BoatIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography fontWeight="bold">Naves ({naves.length})</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ mb: 1, textAlign: 'right' }}>
            <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => abrirDialog('nave')}>
              Nueva Nave
            </Button>
          </Box>
          <DataTable columns={colNaves} data={naves} loading={cargando.naves} actions={editAction('nave')} pagination={false} />
        </AccordionDetails>
      </Accordion>

      {/* CARGOS */}
      <Accordion expanded={expanded === 'cargos'} onChange={() => setExpanded(expanded === 'cargos' ? '' : 'cargos')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <BadgeIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography fontWeight="bold">Cargos ({cargos.length})</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ mb: 1, textAlign: 'right' }}>
            <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => abrirDialog('cargo')}>
              Nuevo Cargo
            </Button>
          </Box>
          <DataTable columns={colCargos} data={cargos} loading={cargando.cargos} actions={[
            { tooltip: 'Editar', icon: <EditIcon fontSize="small" />, onClick: (row) => abrirDialog('cargo', row) },
            { tooltip: 'Eliminar', icon: <DeleteIcon fontSize="small" color="error" />, onClick: (row) => setDeleteConfirm({ open: true, id: row.id }) },
          ]} pagination={false} />
        </AccordionDetails>
      </Accordion>

      {/* TRIPULANTES */}
      <Accordion expanded={expanded === 'tripulantes'} onChange={() => setExpanded(expanded === 'tripulantes' ? '' : 'tripulantes')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <GroupIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography fontWeight="bold">Tripulantes ({tripulantes.length})</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ mb: 1, textAlign: 'right' }}>
            <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => abrirDialog('tripulante')}>
              Nuevo Tripulante
            </Button>
          </Box>
          <DataTable columns={colTripulantes} data={tripulantes} loading={cargando.tripulantes} actions={editAction('tripulante')} />
        </AccordionDetails>
      </Accordion>

      {/* DIALOG */}
      <Dialog open={dialog.open} onClose={cerrarDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialog.data?.id ? 'Editar' : 'Nuevo'} {dialog.tipo === 'nave' ? 'Nave' : dialog.tipo === 'cargo' ? 'Cargo' : 'Tripulante'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {dialog.tipo === 'nave' && (
              <>
                <TextField
                  label="Nombre de la nave" fullWidth required size="small"
                  value={dialog.data?.nombre || ''}
                  onChange={(e) => handleFieldChange('nombre', e.target.value)}
                />
                <FormControlLabel
                  control={<Switch checked={dialog.data?.activo ?? true} onChange={(e) => handleFieldChange('activo', e.target.checked)} />}
                  label="Activa"
                />
              </>
            )}

            {dialog.tipo === 'cargo' && (
              <>
                <TextField
                  label="Nombre del cargo" fullWidth required size="small"
                  value={dialog.data?.nombre || ''}
                  onChange={(e) => handleFieldChange('nombre', e.target.value.toUpperCase())}
                />
                <TextField
                  label="Factor de participación" type="number" fullWidth required size="small"
                  value={dialog.data?.factor_participacion ?? 1.0}
                  onChange={(e) => handleFieldChange('factor_participacion', e.target.value)}
                  inputProps={{ step: 0.25, min: 0.25, max: 5 }}
                  helperText="Ej: Patrón=2.0, Motorista=1.5, Tripulante=1.0"
                />
              </>
            )}

            {dialog.tipo === 'tripulante' && (
              <>
                <TextField
                  label="Nombre completo" fullWidth required size="small"
                  value={dialog.data?.nombre || ''}
                  onChange={(e) => handleFieldChange('nombre', e.target.value)}
                />
                <Grid container spacing={1}>
                  <Grid size={{ xs: 8 }}>
                    <TextField
                      label="RUT (sin DV)" fullWidth required size="small"
                      value={dialog.data?.rut || ''}
                      onChange={(e) => handleFieldChange('rut', e.target.value.replace(/\D/g, ''))}
                    />
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <TextField
                      label="DV" fullWidth required size="small"
                      value={dialog.data?.dv || ''}
                      onChange={(e) => handleFieldChange('dv', e.target.value.slice(0, 1).toUpperCase())}
                      inputProps={{ maxLength: 1 }}
                    />
                  </Grid>
                </Grid>
                <TextField
                  label="Cargo" select fullWidth size="small"
                  value={dialog.data?.cargo_id || ''}
                  onChange={(e) => handleFieldChange('cargo_id', e.target.value)}
                  SelectProps={{ native: true }}
                >
                  <option value="">-- Seleccione --</option>
                  {cargos.map(c => (
                    <option key={c.id} value={c.id}>{c.nombre} (x{c.factor_participacion})</option>
                  ))}
                </TextField>
                <FormControlLabel
                  control={<Switch checked={dialog.data?.activo ?? true} onChange={(e) => handleFieldChange('activo', e.target.checked)} />}
                  label="Activo"
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={cerrarDialog} disabled={guardando}>Cancelar</Button>
          <Button onClick={guardar} variant="contained" disabled={guardando}
            startIcon={guardando ? <CircularProgress size={16} /> : null}>
            {guardando ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <ConfirmDialog
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, id: null })}
        onConfirm={eliminarCargo}
        title="Eliminar Cargo"
        message="Si el cargo está asignado a tripulantes, no se podrá eliminar. ¿Desea continuar?"
        confirmText="Eliminar"
        loading={eliminando}
        severity="error"
      />
    </Box>
  );
};

export default MaestrosTab;
